import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, output, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { TrailApi } from "./trail.api";
import { TrailLabels } from "./trail.labels";
import { nextAppendSortOrder } from "../../../util/sort.order.util";
import {
    includeTreeCohortsHaveRoots,
    includeTreeMappingSummary,
    includeTreeSelectionsComplete,
    toCreateRequirementTree,
    type RequirementGroupNode,
    type RequirementNode,
} from "./trail.requirement.tree.component";
import { TrailRequirementTreeEditorComponent } from "./trail.requirement.tree.editor.component";

type WizardStep = 1 | 2 | 3;

@TypeManagerDecorator("s25-ng-trail-curriculum-create")
@Component({
    selector: "s25-ng-trail-curriculum-create",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25ButtonComponent, TrailRequirementTreeEditorComponent],
    host: { class: "trail-curriculum-create" },
    template: `
        <div class="c-padding--double">
            <div class="trail-curriculum-create__header c-margin-bottom--single">
                <h2>Create {{ labels.CURRICULUM }}</h2>
                <s25-ng-button [type]="'outline'" [onClick]="handleCancel">← Cancel</s25-ng-button>
            </div>

            <div class="trail-curriculum-create__steps c-margin-bottom--double">
                <span [class.trail-curriculum-create__step--active]="currentStep() === 1"
                    >1. Header & {{ labels.COHORTS }}</span
                >
                <span [class.trail-curriculum-create__step--active]="currentStep() === 2"
                    >2. {{ labels.REQUIREMENTS }}</span
                >
                <span [class.trail-curriculum-create__step--active]="currentStep() === 3">3. Confirm</span>
            </div>

            @if (currentStep() === 1) {
                <section class="trail-curriculum-create__step-content">
                    <h3>Step 1: {{ labels.CURRICULUM }} Details & {{ labels.COHORT }} Selection</h3>
                    <div class="trail-curriculum-create__form">
                        <div class="form-group c-margin-bottom--single">
                            <label>Name *</label>
                            <input
                                class="c-input"
                                [ngModel]="formName()"
                                (ngModelChange)="formName.set($event)"
                                placeholder="{{ labels.CURRICULUM }} name"
                                required
                            />
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label>Display Name</label>
                            <input
                                class="c-input"
                                [ngModel]="formDisplayName()"
                                (ngModelChange)="formDisplayName.set($event)"
                                placeholder="Shown to students"
                            />
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label>Description</label>
                            <textarea
                                class="c-input"
                                rows="3"
                                [ngModel]="formDescription()"
                                (ngModelChange)="formDescription.set($event)"
                            ></textarea>
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label>{{ labels.ENDING_AWARD }} Key *</label>
                            <input
                                class="c-input"
                                [ngModel]="formEndingAwardKey()"
                                (ngModelChange)="formEndingAwardKey.set($event)"
                                placeholder="e.g. CERTIFICATE"
                                required
                            />
                        </div>
                        <div class="form-group c-margin-bottom--single">
                            <label>{{ labels.ENDING_AWARD }} Display Name</label>
                            <input
                                class="c-input"
                                [ngModel]="formEndingAwardDisplayName()"
                                (ngModelChange)="formEndingAwardDisplayName.set($event)"
                            />
                        </div>
                    </div>

                    <h4 class="c-margin-top--double c-margin-bottom--half">
                        Select {{ labels.COHORTS }} (multi-select)
                    </h4>
                    @if (cohortsResource.isLoading()) {
                        <p>Loading cohorts…</p>
                    } @else {
                        <div class="trail-curriculum-create__cohort-list">
                            @for (cohort of cohortsResource.value() ?? []; track cohort.cohortId) {
                                <label class="trail-curriculum-create__cohort-item">
                                    <input
                                        type="checkbox"
                                        [checked]="isCohortSelected(cohort.cohortId!)"
                                        (change)="toggleCohort(cohort.cohortId!)"
                                    />
                                    {{ cohort.cohortCode }} ({{ cohort.cohortTypeCode }})
                                </label>
                            }
                        </div>
                    }

                    <div class="trail-curriculum-create__nav c-margin-top--double">
                        <s25-ng-button [type]="'primary'" [onClick]="goToStep2" [disabled]="!canProceedToStep2()">
                            Next: {{ labels.REQUIREMENTS }} →
                        </s25-ng-button>
                    </div>
                </section>
            }

            @if (currentStep() === 2) {
                <section class="trail-curriculum-create__step-content">
                    <h3>Step 2: Build {{ labels.REQUIREMENT }} Tree</h3>
                    <p class="c-text--secondary c-margin-bottom--single">
                        {{ labels.ENDING_AWARD }} criteria — same requirement types as a
                        {{ labels.TRAIL.toLowerCase() }}, including {{ labels.SUMMIT }}s. Students must satisfy this
                        tree to earn the {{ labels.ENDING_AWARD }}.
                    </p>
                    <s25-ng-trail-requirement-tree-editor
                        [cohortIds]="selectedCohortIdList()"
                        [node]="requirementTree()"
                        [includeTrails]="false"
                        [domain]="'COCURRICULAR'"
                        (nodeChange)="onTreeChange($event)"
                    />
                    <div class="trail-curriculum-create__nav c-margin-top--double">
                        <s25-ng-button [type]="'outline'" [onClick]="goToStep1">← Back</s25-ng-button>
                        <s25-ng-button [type]="'primary'" [onClick]="goToStep3" [disabled]="!canProceedToStep3()">
                            Next: Confirm →
                        </s25-ng-button>
                    </div>
                </section>
            }

            @if (currentStep() === 3) {
                <section class="trail-curriculum-create__step-content">
                    <h3>Step 3: Review & Create</h3>
                    <div class="trail-curriculum-create__summary c-margin-bottom--double">
                        <dl class="trail-curriculum-create__summary-dl">
                            <dt>Name</dt>
                            <dd>{{ formName() }}</dd>
                            <dt>Display Name</dt>
                            <dd>{{ formDisplayName() || "—" }}</dd>
                            <dt>{{ labels.ENDING_AWARD }}</dt>
                            <dd>{{ formEndingAwardKey() }}</dd>
                            <dt>{{ labels.COHORTS }}</dt>
                            <dd>{{ selectedCohortSummary() }}</dd>
                            <dt>{{ labels.REQUIREMENTS }}</dt>
                            <dd>{{ requirementTreeSummary() }}</dd>
                            @if (includeTreeConfirmLines().length) {
                                <dt>Include trees</dt>
                                <dd>
                                    @for (line of includeTreeConfirmLines(); track line) {
                                        <div>{{ line }}</div>
                                    }
                                </dd>
                            }
                        </dl>
                    </div>
                    <div class="trail-curriculum-create__nav">
                        <s25-ng-button [type]="'outline'" [onClick]="goToStep2">← Back</s25-ng-button>
                        <s25-ng-button
                            [type]="'primary'"
                            [onClick]="createCurriculum"
                            [disabled]="creating() || !canProceedToStep3()"
                        >
                            {{ creating() ? "Creating…" : "Create " + labels.CURRICULUM }}
                        </s25-ng-button>
                    </div>
                </section>
            }
        </div>
    `,
    styles: `
        .trail-curriculum-create__header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 1rem;
        }
        .trail-curriculum-create__steps {
            display: flex;
            gap: 2rem;
            font-size: 0.9em;
            color: var(--color--text-muted, #666);
        }
        .trail-curriculum-create__step--active {
            font-weight: 700;
            color: var(--color--text, #000);
        }
        .trail-curriculum-create__form .form-group {
            display: grid;
            grid-template-columns: 14rem minmax(0, 1fr);
            gap: 0.75rem;
            align-items: start;
            max-width: 48rem;
        }
        .trail-curriculum-create__form .form-group > label {
            padding-top: 0.35rem;
        }
        .trail-curriculum-create__cohort-list {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem 1.5rem;
        }
        .trail-curriculum-create__cohort-item {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            cursor: pointer;
        }
        .trail-curriculum-create__nav {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-curriculum-create__summary-dl {
            display: grid;
            grid-template-columns: 10rem 1fr;
            gap: 0.5rem 1rem;
        }
        .trail-curriculum-create__summary-dl dt {
            font-weight: 600;
        }
    `,
})
export class TrailCurriculumCreateComponent {
    readonly labels = TrailLabels;
    readonly created = output<void>();
    readonly cancel = output<void>();

    readonly currentStep = signal<WizardStep>(1);
    readonly creating = signal(false);

    readonly formName = signal("");
    readonly formDisplayName = signal("");
    readonly formDescription = signal("");
    readonly formEndingAwardKey = signal("");
    readonly formEndingAwardDisplayName = signal("");
    readonly selectedCohortIds = signal<Set<number>>(new Set());
    readonly selectedCohortIdList = computed(() => [...this.selectedCohortIds()].sort((left, right) => left - right));
    readonly treeEditor = viewChild(TrailRequirementTreeEditorComponent);

    readonly requirementTree = signal<RequirementGroupNode>({
        nodeType: "group",
        groupType: "ALL",
        label: "Root",
        children: [],
    });

    readonly cohortsResource = s25Resource({
        loader: () => TrailApi.getCohorts(),
    });

    readonly curriculaResource = s25Resource({
        loader: () => TrailApi.listCurricula(),
    });

    readonly includeTreeCohorts = computed(() => this.treeEditor()?.includeTreeCohorts() ?? []);

    readonly canProceedToStep2 = computed(() => {
        return (
            this.formName().trim().length > 0 &&
            this.formEndingAwardKey().trim().length > 0 &&
            this.selectedCohortIds().size > 0
        );
    });

    readonly selectedCohortSummary = computed(() => {
        const cohorts = this.cohortsResource.value() ?? [];
        const selectedIds = this.selectedCohortIds();
        const selectedCodes = cohorts
            .filter((cohort) => cohort.cohortId != null && selectedIds.has(cohort.cohortId))
            .map((cohort) => cohort.cohortCode);
        return selectedCodes.length ? selectedCodes.join(", ") : "None selected";
    });

    readonly requirementTreeSummary = computed(() => {
        const tree = this.requirementTree();
        const childCount = tree.children.length;
        return `${tree.groupType} group with ${childCount} child${childCount !== 1 ? "ren" : ""}`;
    });

    readonly includeTreeConfirmLines = computed(() => {
        return includeTreeMappingSummary(this.requirementTree(), this.includeTreeCohorts());
    });

    readonly canProceedToStep3 = computed(() => {
        const cohortIds = [...this.selectedCohortIds()];
        const tree = this.requirementTree();
        const includeLeaves = includeTreeMappingSummary(tree, this.includeTreeCohorts());
        if (!includeLeaves.length) return true;
        return includeTreeCohortsHaveRoots(this.includeTreeCohorts()) && includeTreeSelectionsComplete(tree, cohortIds);
    });

    isCohortSelected(cohortId: number): boolean {
        return this.selectedCohortIds().has(cohortId);
    }

    toggleCohort(cohortId: number) {
        this.selectedCohortIds.update((currentSet) => {
            const updatedSet = new Set(currentSet);
            if (updatedSet.has(cohortId)) {
                updatedSet.delete(cohortId);
            } else {
                updatedSet.add(cohortId);
            }
            return updatedSet;
        });
    }

    onTreeChange(updatedNode: RequirementNode) {
        if (updatedNode.nodeType === "group") {
            this.requirementTree.set(updatedNode as RequirementGroupNode);
        }
    }

    goToStep1 = () => this.currentStep.set(1);
    goToStep2 = () => this.currentStep.set(2);
    goToStep3 = () => this.currentStep.set(3);

    handleCancel = () => {
        this.cancel.emit();
    };

    createCurriculum = async () => {
        if (this.creating()) return;
        this.creating.set(true);
        try {
            const cohortIds = [...this.selectedCohortIds()];
            const response = await TrailApi.createCurriculum({
                cohortIds,
                name: this.formName().trim(),
                displayName: this.formDisplayName().trim() || undefined,
                description: this.formDescription().trim() || undefined,
                endingAwardKey: this.formEndingAwardKey().trim(),
                endingAwardDisplayName: this.formEndingAwardDisplayName().trim() || undefined,
                catalogSortOrder: nextAppendSortOrder(
                    (this.curriculaResource.value() ?? []).map((curriculum) => curriculum.catalogSortOrder),
                ),
                requirementTree: toCreateRequirementTree(this.requirementTree()),
            });
            const skipped = response.skipped ?? [];
            if (skipped.length) {
                const reasons = skipped.map((skip) => skip.reason).join("\n");
                await AlertService.alert(`Created with ${skipped.length} skip(s):\n${reasons}`);
            }
            this.created.emit();
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.creating.set(false);
        }
    };
}
